import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { fetchDirectionBySlug } from '@api/directions'
import { ErrorPage } from '@components/ui/ErrorPage'
import { InDevelopmentPage } from '@components/ui/InDevelopmentPage'
import { DirectionPage } from './DirectionPage'
import type { DirectionPageProps } from './DirectionPage'
import { useLang } from '@shared/i18n/useLang'
import { ssrCache } from '@shared/lib/ssrCache'

export function DirectionPageLoader() {
  const { slug } = useParams<{ slug: string }>()
  const lang = useLang()
  const cacheKey = slug ? `direction:${lang}:${slug}` : null
  const seeded = cacheKey != null && ssrCache.has(cacheKey)

  const [data, setData] = useState<DirectionPageProps | null | 'loading' | 'error'>(
    () => (seeded ? (ssrCache.get<DirectionPageProps | null>(cacheKey!) ?? null) : 'loading'),
  )
  const [errorMsg, setErrorMsg] = useState<string>('')
  const skipNextFetch = useRef(seeded)

  useEffect(() => {
    if (!slug) return
    if (skipNextFetch.current) { skipNextFetch.current = false; return }
    setData('loading')
    setErrorMsg('')
    fetchDirectionBySlug(slug)
      .then(res => setData(res ?? null))
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err)
        console.error('[DirectionPageLoader] fetch failed:', err)
        setErrorMsg(msg)
        setData('error')
      })
  }, [slug])

  if (data === 'loading') {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          border: '3px solid #D9DEE2',
          borderTopColor: '#189BD5',
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    )
  }

  if (data === 'error') return (
    <>
      <ErrorPage code="500" />
      {import.meta.env.DEV && errorMsg && (
        <div style={{ position: 'fixed', bottom: 16, left: 16, right: 16, background: '#1e1e1e', color: '#ff6b6b', fontFamily: 'monospace', fontSize: 12, padding: '12px 16px', borderRadius: 4, zIndex: 9999, wordBreak: 'break-all', maxHeight: 120, overflow: 'auto' }}>
          <strong>DEV error:</strong> {errorMsg}
        </div>
      )}
    </>
  )

  if (!data) return <ErrorPage code="404" />

  if (data.dev) return <InDevelopmentPage />

  return <DirectionPage {...data} />
}
